import type { ReactNode } from 'react';
import {
  STORAGE_PRESETS, STORAGE_WORM_LABEL, STORAGE_WORM_MODES,
  type DocumentStorageSettings, type StoragePresetKey, type StorageWormMode,
} from '@pqrs/contracts';
import { SelectorSimple } from '@xiltrion/ui/bloques/selectores';
import { Input } from '@xiltrion/ui/input';
import { Label } from '@xiltrion/ui/label';
import { Switch } from '@xiltrion/ui/switch';
import { Field } from './shared';

/** Formulario de un bucket compatible con S3: el almacenamiento principal (Almacenamiento) y el respaldo (Respaldo). */
export interface BucketForm {
  preset: StoragePresetKey;
  endpoint: string;
  region: string;
  bucket: string;
  prefix: string;
  forcePathStyle: boolean;
  accessKeyId: string;
  /** Llave nueva (vacía = se conserva la guardada). */
  secret: string;
  wormMode: StorageWormMode;
  wormDefaultDays: string;
}

type SavedBucket = Omit<DocumentStorageSettings, 'wormDefaultDays'> & { wormDefaultDays?: number };

export function initialBucketForm(s: SavedBucket | null): BucketForm {
  if (!s) return { preset: 'aws', endpoint: '', region: 'us-east-1', bucket: '', prefix: '', forcePathStyle: false, accessKeyId: '', secret: '', wormMode: 'off', wormDefaultDays: '3650' };
  return { preset: s.preset, endpoint: s.endpoint, region: s.region, bucket: s.bucket, prefix: s.prefix, forcePathStyle: s.forcePathStyle, accessKeyId: s.accessKeyId, secret: '', wormMode: s.wormMode, wormDefaultDays: String(s.wormDefaultDays ?? 3650) };
}

/** Lo visible del formulario, con la misma forma que lo guardado (para saber si la llave guardada aplica). */
export function bucketSettingsOf(f: BucketForm): DocumentStorageSettings {
  return {
    provider: 'S3', preset: f.preset, endpoint: f.endpoint.trim().replace(/\/+$/, ''), region: f.region.trim(), bucket: f.bucket.trim(), prefix: f.prefix.trim().replace(/^\/+|\/+$/g, ''),
    forcePathStyle: f.forcePathStyle, accessKeyId: f.accessKeyId.trim(), wormMode: f.wormMode, wormDefaultDays: Number(f.wormDefaultDays) || 3650,
  };
}

/** Lo que falta para guardar o probar, o null. */
export function bucketMissing(f: BucketForm, hasSecret: boolean, opts: { retentionDays?: boolean } = {}): string | null {
  if (f.preset !== 'aws' && !f.endpoint.trim()) return 'el endpoint';
  if (f.endpoint.trim() && !/^https?:\/\/[^\s/<>]+$/.test(f.endpoint.trim().replace(/\/+$/, ''))) return 'un endpoint válido (https://servidor, sin «<…>»)';
  if (!f.region.trim()) return 'la región';
  if (!/^[a-z0-9][a-z0-9.-]{1,61}[a-z0-9]$/.test(f.bucket.trim())) return 'un nombre de bucket válido';
  if (!f.accessKeyId.trim()) return 'la llave de acceso';
  if (!hasSecret) return 'la llave secreta';
  const days = Number(f.wormDefaultDays);
  if (opts.retentionDays && f.wormMode !== 'off' && (!Number.isInteger(days) || days < 1)) return 'los días de retención provisional';
  return null;
}

const WORM_OPTIONS = STORAGE_WORM_MODES.map((value) => ({ value, label: STORAGE_WORM_LABEL[value] }));
const PRESET_OPTIONS = Object.entries(STORAGE_PRESETS).map(([value, p]) => ({ value, label: p.label }));

/**
 * Campos del bucket: servicio, endpoint y región, bucket y carpeta, llaves y WORM. `locked` impide cambiar el lugar
 * (servicio, bucket, carpeta); `retentionDays` muestra la retención provisional (solo el principal la calcula).
 */
export function StorageBucketFields({ id, f, set, locked = false, storedSecretApplies, retentionDays = false, wormHint, requirements, presetHint }: {
  id: string;
  f: BucketForm;
  set: (patch: Partial<BucketForm>) => void;
  locked?: boolean;
  storedSecretApplies: boolean;
  retentionDays?: boolean;
  wormHint: string;
  /** Lo que necesita el bucket, entre el servicio y los datos. */
  requirements?: ReactNode;
  /** Otra ayuda para un servicio (el respaldo no pide permiso de borrar). */
  presetHint?: Partial<Record<StoragePresetKey, string>>;
}) {
  const preset = STORAGE_PRESETS[f.preset];
  return (
    <>
      <Field label="Servicio" htmlFor={`${id}-preset`} hint={presetHint?.[f.preset] ?? preset.hint}>
        <SelectorSimple id={`${id}-preset`} value={f.preset} options={PRESET_OPTIONS} disabled={locked}
          onChange={(k) => { if (!k) return; const p = STORAGE_PRESETS[k as StoragePresetKey]; set({ preset: k as StoragePresetKey, endpoint: p.endpoint, region: p.region, forcePathStyle: p.forcePathStyle }); }} />
      </Field>
      {requirements}

      <div className="grid sm:grid-cols-[1fr_10rem] gap-3">
        <Field label="Endpoint" htmlFor={`${id}-endpoint`} required={f.preset !== 'aws'} hint={f.preset === 'aws' ? 'Vacío: Amazon S3 lo deduce de la región.' : 'La dirección del servicio, con https://.'}><Input id={`${id}-endpoint`} autoComplete="off" placeholder="https://s3.entidad.gov.co" value={f.endpoint} disabled={locked} onChange={(e) => set({ endpoint: e.target.value })} /></Field>
        <Field label="Región" htmlFor={`${id}-region`} required><Input id={`${id}-region`} autoComplete="off" value={f.region} disabled={locked} onChange={(e) => set({ region: e.target.value })} /></Field>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Bucket" htmlFor={`${id}-bucket`} required hint="Minúsculas, dígitos, puntos y guiones."><Input id={`${id}-bucket`} autoComplete="off" placeholder="expedientes-pqrs" value={f.bucket} disabled={locked} onChange={(e) => set({ bucket: e.target.value })} /></Field>
        <Field label="Carpeta" htmlFor={`${id}-prefix`} hint="Opcional: una carpeta dentro del bucket, por ejemplo pqrs."><Input id={`${id}-prefix`} autoComplete="off" value={f.prefix} disabled={locked} onChange={(e) => set({ prefix: e.target.value })} /></Field>
      </div>
      <div className="flex items-center gap-2"><Switch id={`${id}-path`} checked={f.forcePathStyle} onCheckedChange={(c) => set({ forcePathStyle: !!c })} /><Label htmlFor={`${id}-path`} className="font-normal">Bucket en la ruta (host/bucket), como piden MinIO y otros servidores propios</Label></div>

      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Llave de acceso (access key id)" htmlFor={`${id}-key`} required><Input id={`${id}-key`} autoComplete="off" value={f.accessKeyId} onChange={(e) => set({ accessKeyId: e.target.value })} /></Field>
        <Field label="Llave secreta" htmlFor={`${id}-secret`} required={!storedSecretApplies} hint={storedSecretApplies ? 'Hay una guardada; escriba otra solo para cambiarla.' : undefined}>
          <Input id={`${id}-secret`} type="password" autoComplete="new-password" placeholder={storedSecretApplies ? '••••••••' : ''} value={f.secret} onChange={(e) => set({ secret: e.target.value })} />
        </Field>
      </div>

      <div className={retentionDays ? 'grid sm:grid-cols-[1fr_12rem] gap-3' : 'grid gap-3'}>
        <Field label="WORM (escritura única)" htmlFor={`${id}-worm`} hint={wormHint}><SelectorSimple id={`${id}-worm`} value={f.wormMode} options={WORM_OPTIONS} onChange={(m) => m && set({ wormMode: m as StorageWormMode })} /></Field>
        {retentionDays && <Field label="Retención provisional (días)" htmlFor={`${id}-days`} hint="Al cierre se extiende según la TRD."><Input id={`${id}-days`} className="tnum" type="number" inputMode="numeric" min={1} max={36500} value={f.wormDefaultDays} disabled={f.wormMode === 'off'} onChange={(e) => set({ wormDefaultDays: e.target.value })} /></Field>}
      </div>
    </>
  );
}
